import { useState } from 'react';
import { Badge, Card, EmptyState, ErrorState, Pager, Spinner, Table, Td, Th, cx } from '@/components/ui';
import { changeColorClass, formatDate, formatKrw, formatPercent, formatUsdPrice } from '@/lib/format';
import { AccountNav } from './AccountNav';
import { bandPriceFormat } from './bandCurrency';
import type { EntryIndicators, MyTrade } from './stockTypes';
import { useEntryIndicators, useMyTrades, useUsTaxEstimate } from './useStocks';
import { StockLink } from './StockLink';

/**
 * 매매일지 - 내가 실제로 사고판 기록. (13단계)
 *
 * 🔴 AI 모의투자와 절대 같은 표에 놓지 않는다
 *   저쪽은 배치가 굴린 가상 계좌이고 이쪽은 사용자가 토스 앱에서 낸 실제 주문이다.
 *   두 값이 갈리는 것이 정상이고, 그 차이가 곧 타이밍을 놓친 몫이다. (문서 8.3절)
 *
 * 🔴 손익은 수수료와 세금을 뺀 순손익이다
 *   그러지 않으면 "벌었다고 생각했는데 계좌는 안 늘어난" 상태가 화면에 남는다.
 */

/** 기간 선택지. 값은 일 수다. */
const PERIODS = [
  { days: 30, label: '최근 30일' },
  { days: 90, label: '최근 90일' },
  { days: 365, label: '최근 1년' },
];

const PAGE_SIZE = 10;

export function MyTradesPage() {
  const [days, setDays] = useState(30);
  const [page, setPage] = useState(0);
  const query = useMyTrades(days);
  // 산 날 시스템 지표(150번 8번) — 따로 부른다. 실패해도 일지는 그대로 그린다(줄 · 칸만 빠진다)
  const indicators = useEntryIndicators(days).data;
  const indicatorOf = new Map((indicators?.items ?? []).map((i) => [`${i.symbol}-${i.boughtAt}`, i]));

  const data = query.data;
  const closed = data?.closed ?? [];
  const totalPages = Math.max(1, Math.ceil(closed.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const visible = closed.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  return (
    <div className="flex flex-col gap-4">
      <AccountNav />

      <header>
        <h1 className="text-xl font-semibold">매매일지</h1>
        <p className="mt-1 text-sm text-fg-muted">
          토스 계좌의 실제 체결을 먼저 산 것부터 짝지어 왕복 거래로 묶습니다. 손익은
          수수료와 세금을 뺀 순손익입니다. AI 모의투자와는 다른 기록입니다. 청산 줄 아래 회색 글씨는 산 날의
          시스템 지표(120일 범위 위치 · 20일 이격 · 저점매수 감시 여부)이고, "산 자리별 성적" 이 그 위치로 갈라 셉니다.
        </p>
      </header>

      <div className="flex gap-1">
        {PERIODS.map((option) => (
          <button
            key={option.days}
            type="button"
            onClick={() => {
              setDays(option.days);
              setPage(0);
            }}
            className={cx(
              'rounded-lg px-3 py-1.5 text-sm',
              days === option.days
                ? 'bg-brand text-brand-fg'
                : 'text-fg-muted hover:bg-surface-hover',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      {query.isLoading && <Spinner label="매매일지를 불러오는 중" />}
      {query.error && (
        <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
      )}

      {data && closed.length === 0 && data.openLots.length === 0 && (
        <Card>
          <EmptyState
            title="체결 기록이 없습니다"
            description="토스 체결이 동기화되면 여기에 쌓입니다. 바로 받으려면 계좌 화면의 [지금 동기화]를 누르세요(그 화면을 열어 두면 1분마다, 배치는 평일 16:00·20:10과 매일 06:10(미국장 뒤)). 이 화면은 5분 안에 다시 열면 앞서 읽은 것을 보여 줍니다. 며칠이 지나도 비어 있으면 연동된 계좌가 실제로 매매하는 계좌인지 확인하세요."
          />
        </Card>
      )}

      {data && (closed.length > 0 || data.openLots.length > 0) && (
        <>
          <Card title="요약">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {/*
                🔴 금액은 통화별로 따로 그린다 (2026-10-17 Q23) — 원화 줄은 늘, 달러 줄은 기간 안 달러 청산이 있을 때만
                  (서버가 그때만 …Usd 를 채운다). 원화로 환산해 한 숫자로 합치지 않는다 — 환율 시점이 섞인다.
              */}
              <Stat
                label="실현손익"
                value={formatKrw(data.realizedPnl)}
                className={changeColorClass(data.realizedPnl)}
                usdValue={data.realizedPnlUsd}
              />
              <Stat
                label="승률"
                value={data.winRate === null ? '—' : `${data.winRate}%`}
                sub={`${data.winCount}승 ${data.lossCount}패`}
              />
              <Stat
                label="추천과 이어진 건"
                value={`${data.recoMatched}건`}
                sub={formatKrw(data.recoPnl)}
                usdSub={data.recoPnlUsd}
              />
              <Stat
                label="스스로 산 건"
                value={`${closed.length - data.recoMatched}건`}
                sub={formatKrw(data.selfPnl)}
                usdSub={data.selfPnlUsd}
              />
            </div>
            {/*
              🔴 이 비교가 "추천이 도움이 됐나" 에 답한다 (문서 8.4절).
                다만 표본이 적을 때는 우연이 더 크다는 것도 함께 말한다.
            */}
            <p className="mt-3 text-xs text-fg-subtle">
              추천과 이어진 건은 매수 5일 안에 같은 종목 추천이 있었던 경우입니다. 그
              추천을 보고 샀다는 증거는 아니며, 건수가 적을 때는 우연이 더 큽니다.
              {data.realizedPnlUsd !== null &&
                ' 원화 거래와 달러(미국) 거래의 손익은 따로 더해 두 줄로 보입니다 — 환율로 바꿔 한 숫자로 합치지 않습니다(어느 날 환율을 쓰느냐에 따라 값이 달라집니다). 건수와 승률은 둘을 함께 셉니다.'}
            </p>
          </Card>

          {indicators && closed.length > 0 && <EntryBucketCard buckets={indicators.buckets} />}
          <UsTaxCard />

          {closed.length > 0 && (
            <Card title={`청산 ${closed.length}건`}>
              <Table>
                <thead>
                  <tr>
                    <Th>종목</Th>
                    <Th align="right">매수 → 매도</Th>
                    <Th align="right">순손익</Th>
                    <Th align="right">보유</Th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((trade) => (
                    <TradeRow
                      key={`${trade.symbol}-${trade.boughtAt}-${trade.soldAt}`}
                      trade={trade}
                      indicator={indicatorOf.get(`${trade.symbol}-${trade.boughtAt}`)}
                    />
                  ))}
                </tbody>
              </Table>
              <Pager page={safePage} totalPages={totalPages} onChange={setPage} />
            </Card>
          )}

          {data.openLots.length > 0 && (
            <Card title={`미청산 ${data.openLots.length}건`}>
              <Table>
                <thead>
                  <tr>
                    <Th>종목</Th>
                    <Th align="right">매수일</Th>
                    <Th align="right">단가 × 수량</Th>
                    <Th align="right">매수금액</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.openLots.map((lot) => (
                    <tr key={`${lot.symbol}-${lot.boughtAt}`}>
                      <Td>
                        <StockLink symbol={lot.symbol}>{lot.stockName ?? lot.symbol}</StockLink>
                      </Td>
                      <Td align="right">{formatDate(lot.boughtAt)}</Td>
                      {/* 단가 · 금액은 매수 체결 통화로 — 미국 매수분은 달러(2026-10-17 Q23) */}
                      <Td align="right">
                        {bandPriceFormat(lot.currency).px(lot.buyPrice)} × {lot.quantity}
                      </Td>
                      <Td align="right" numeric>{bandPriceFormat(lot.currency).px(lot.buyAmount)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <p className="mt-3 text-xs text-fg-subtle">
                체결 이력을 짝짓고 남은 매수분입니다. 계좌 화면의 보유 종목과 다르면 체결
                동기화에 빠진 구간이 있다는 뜻입니다.
              </p>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

/**
 * 미국 주식 양도세 어림 — 해마다 한 줄. (2026-10-16, docs/impl/150 9번 · GPT [USTAX-R1])
 *
 * 🔴 "납부 예정 세액" 이 아니라 "어림" 이다 — 경고 문구를 빼지 않는다(GPT Q5).
 * 공제 · 세율은 서버가 준 값으로 그린다(숫자를 문구에 박지 않는다 · I-10).
 * 귀속 연도는 매도 **결제일**의 해 — 12/31 밤에 판 미국 주식은 다음 해로 갈 수 있다.
 */
function UsTaxCard() {
  const { data } = useUsTaxEstimate();
  if (!data) {
    return null;
  }
  const rate = `${Math.round(data.taxRate * 100)}%`;
  return (
    <Card title="미국 주식 양도세 어림">
      {data.years.length === 0 ? (
        <p className="text-sm text-fg-muted">토스로 판 미국 주식이 아직 없습니다.</p>
      ) : (
        <ul className="space-y-2">
          {[...data.years].reverse().map((y) => (
            <li key={y.year} className="text-sm">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-medium text-fg">{y.year}년</span>
                <span className="text-fg-muted">어림 세액</span>
                <span className="tabular font-medium text-fg">{formatKrw(y.estimatedTaxKrw)}</span>
              </div>
              <p className="text-xs text-fg-subtle">
                판 것 {y.trades}건 · 이익 {formatKrw(y.gainKrw)} · 손실 {formatKrw(y.lossKrw)} · 합 {formatKrw(y.netKrw)} − 공제{' '}
                {formatKrw(data.deduction)} → 과세 {formatKrw(y.taxableKrw)} × {rate}
                {y.fxMissing > 0 && ` · 환율이 없어 뺀 것 ${y.fxMissing}건`}
                {y.settlementFallback > 0 && ` · 결제일 없어 체결일로 본 것 ${y.settlementFallback}건`}
              </p>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-xs text-fg-subtle">
        예상 세액입니다. 같은 해 미국 주식 이익 · 손실을 합치고(국내 소액주주 주식은 섞지 않습니다) 기본공제를 뺀 뒤 {rate}(지방소득세 포함)를 곱했습니다.
        원화 환산은 매수 · 매도 결제일의 시장 USD/KRW 값이라 신고에 쓰는 기준환율과 차이가 날 수 있습니다. 증권사의 취득가 계산 방식, 다른 증권사 거래,
        기업행위 · 증여 · 국내시장 복귀계좌(RIA) 등으로 실제 신고액은 달라질 수 있습니다. 최종 신고는 다음 해 5월 홈택스 신고 자료를 확인하세요.
      </p>
    </Card>
  );
}

/**
 * 산 자리별 성적 — 산 날 120일 범위 위치 20% 이하 / 초과 / 계산 못 함. (2026-10-16, docs/impl/150 8번)
 * "나는 어떤 자리에서 잘 사고 어디서 잃나"(문서 8.4절)의 첫 칸이다. 10건 미만이면 승률을 숨긴다(우연이 크다).
 */
function EntryBucketCard({ buckets }: { buckets: EntryIndicators['buckets'] }) {
  return (
    <Card title="산 자리별 성적">
      <div className="grid gap-3 sm:grid-cols-3">
        {buckets.map((bucket) => (
          <Stat
            key={bucket.label}
            label={bucket.label}
            value={`${bucket.count}건`}
            sub={[
              bucket.winRate === null ? (bucket.count > 0 ? `${bucket.wins}승 · 승률은 10건부터` : '') : `승률 ${bucket.winRate}%`,
              bucket.pnl === null ? '' : formatKrw(bucket.pnl),
            ].filter(Boolean).join(' · ')}
          />
        ))}
      </div>
      <p className="mt-3 text-xs text-fg-subtle">
        위치는 산 날 전날까지 120거래일 저가 최저 ~ 고가 최고 사이에서 전날 종가가 어디였나입니다(0 = 바닥 · 100 = 꼭대기 · 그날 아침 시스템이 알던 값). 20일 이격은 전날 종가가 20일 평균보다 몇 % 위 · 아래였나입니다. 국내 종목만 계산합니다(미국 · ETF 등 목록 밖은 "계산 못 함").
      </p>
    </Card>
  );
}

function TradeRow({ trade, indicator }: { trade: MyTrade; indicator?: EntryIndicators['items'][number] }) {
  // 단가 · 순손익 · 추천 목표는 체결 통화로 — 원화는 전과 같은 formatKrw, 달러는 formatUsdPrice(2026-10-17 Q23 · 저점매수 화면과 같은 부품)
  const px = bandPriceFormat(trade.currency).px;
  return (
    <tr>
      <Td>
        <StockLink symbol={trade.symbol} className="font-medium">
          {trade.stockName ?? trade.symbol}
        </StockLink>
        {trade.profile && (
          <Badge tone="info">
            <span className="text-[10px]">추천</span>
          </Badge>
        )}
        {trade.recoTarget !== null && (
          <span className="block text-xs text-fg-subtle">
            추천 목표 {px(trade.recoTarget)}
          </span>
        )}
        {indicator && indicator.position120 !== null && (
          <span className="block text-xs text-fg-subtle">
            산 날 120일 범위 {Math.round(indicator.position120)}%
            {indicator.disparity20 !== null && ` · 20일 이격 ${indicator.disparity20 > 0 ? '+' : ''}${indicator.disparity20}%`}
            {indicator.watched && ' · 저점매수 감시 중이었음'}
          </span>
        )}
      </Td>
      <Td align="right">
        <span className="tabular">
          {px(trade.buyPrice)} → {px(trade.sellPrice)}
        </span>
        <span className="block text-xs text-fg-subtle">
          {formatDate(trade.boughtAt)} → {formatDate(trade.soldAt)} · {trade.quantity}주
        </span>
      </Td>
      <Td align="right">
        <span className={changeColorClass(trade.realizedPnl)}>
          {px(trade.realizedPnl)}
        </span>
        <span className="block text-xs text-fg-subtle">
          {formatPercent(trade.returnPercent)}
        </span>
      </Td>
      <Td align="right">{trade.holdingDays}일</Td>
    </tr>
  );
}

function Stat({
  label,
  value,
  sub,
  className,
  usdValue = null,
  usdSub = null,
}: {
  label: string;
  value: string;
  sub?: string;
  className?: string;
  /** 값(원화) 아래 달러 줄 — null 이면 그리지 않는다(달러 청산 0건 · 2026-10-17 Q23) */
  usdValue?: number | null;
  /** 보조 줄(원화) 아래 달러 줄 — null 이면 그리지 않는다 */
  usdSub?: number | null;
}) {
  return (
    <div>
      <p className="text-xs text-fg-subtle">{label}</p>
      <p className={cx('mt-0.5 text-lg tabular', className)}>{value}</p>
      {usdValue !== null && (
        <p className={cx('text-lg tabular', changeColorClass(usdValue))}>{formatUsdPrice(usdValue)}</p>
      )}
      {sub && <p className="text-xs text-fg-subtle">{sub}</p>}
      {usdSub !== null && <p className="text-xs tabular text-fg-subtle">{formatUsdPrice(usdSub)}</p>}
    </div>
  );
}
